// components/ServiceHDSCreateModal/index.jsx
import React, { useState, useRef } from 'react';
import {
    FaCloudUploadAlt,
    FaFileImage,
    FaTimes,
    FaCheck,
    FaInfoCircle,
    FaUpload,
    FaSpinner
} from 'react-icons/fa';
import { Badge } from 'react-bootstrap';
import useServiceHDSCreate from './hooks/useServiceHDSCreate';
import '../../../assets/css/ServiceHDSPage.css';

const ServiceHDSCreateModal = ({ isOpen, onClose, onServiceCreated }) => {
    const {
        newService,
        isCreating,
        isUploading,
        selectedFile,
        imagePreview,
        fileInputRef,
        handleInputChange,
        handleFileSelect,
        handleRemoveImage,
        handleSubmit,
        handleBackdropClick,
        handleClose
    } = useServiceHDSCreate(onServiceCreated, onClose);

    // États pour le drag & drop
    const [isDragOver, setIsDragOver] = useState(false);
    const dropZoneRef = useRef(null);

    // Si le modal n'est pas ouvert, ne rien afficher
    if (!isOpen) return null;

    // Gestion du drag & drop
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Simuler un événement de changement de fichier
            const event = {
                target: {
                    files: [file]
                }
            };
            handleFileSelect(event);
        }
    };

    return (
        <>
            {/* Modal principal - Style Bottom Sheet */}
            <div
                className="service-create-modal-overlay show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 1050,
                    overflowY: 'auto'
                }}
                onClick={handleBackdropClick}
            >
                <div
                    className="service-create-modal-container"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh',
                        overflowY: 'auto',
                        animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe sticky */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 100,
                        backgroundColor: 'white',
                        borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px',
                        borderTopRightRadius: '24px',
                        padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaCloudUploadAlt className="me-2 text-success" />
                                    Créer un nouveau service
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    Remplissez les informations ci-dessous
                                </p>
                            </div>
                            <button
                                className="btn-close"
                                onClick={handleClose}
                                disabled={isCreating || isUploading}
                            ></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Upload d'image - Version Drop Zone */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaFileImage className="me-2" />
                                Image du service
                            </h5>

                            {/* Drop Zone */}
                            <div
                                ref={dropZoneRef}
                                className={`drop-zone ${isDragOver ? 'drop-zone-active' : ''} ${selectedFile ? 'drop-zone-has-image' : ''}`}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                style={{
                                    border: `2px dashed ${isDragOver ? '#28a745' : '#cbd5e0'}`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    textAlign: 'center',
                                    backgroundColor: isDragOver ? 'rgba(40, 167, 69, 0.05)' : 'white',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    minHeight: '200px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/svg+xml"
                                    onChange={handleFileSelect}
                                    className="file-input"
                                    disabled={isCreating || isUploading}
                                    style={{ display: 'none' }}
                                />

                                {selectedFile ? (
                                    // Aperçu avec overlay
                                    <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
                                        <img
                                            src={imagePreview}
                                            alt="Aperçu du service"
                                            style={{
                                                width: '100%',
                                                maxHeight: '200px',
                                                objectFit: 'contain',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            display: 'flex',
                                            gap: '8px'
                                        }}>
                                            <Badge bg="success" className="rounded-pill px-3 py-2">
                                                <FaCheck className="me-1" /> Sélectionnée
                                            </Badge>
                                        </div>
                                        <button
                                            className="btn btn-danger btn-sm position-absolute"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage();
                                            }}
                                            type="button"
                                            disabled={isCreating || isUploading}
                                            style={{
                                                position: 'absolute',
                                                top: '8px',
                                                left: '8px',
                                                width: '32px',
                                                height: '32px',
                                                padding: 0,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <FaTimes size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    // État vide
                                    <>
                                        <FaCloudUploadAlt
                                            size={48}
                                            style={{
                                                color: isDragOver ? '#28a745' : '#a0aec0',
                                                marginBottom: '12px',
                                                transition: 'color 0.3s ease'
                                            }}
                                        />
                                        <p style={{
                                            fontWeight: 500,
                                            color: isDragOver ? '#28a745' : '#4a5568',
                                            marginBottom: '4px'
                                        }}>
                                            {isDragOver ? 'Déposez votre image ici' : 'Glissez-déposez votre image'}
                                        </p>
                                        <p style={{
                                            fontSize: '14px',
                                            color: '#a0aec0',
                                            marginBottom: '12px'
                                        }}>
                                            ou cliquez pour parcourir
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                <FaFileImage className="me-1" /> JPEG
                                            </Badge>
                                            <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                <FaFileImage className="me-1" /> PNG
                                            </Badge>
                                            <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                <FaFileImage className="me-1" /> WEBP
                                            </Badge>
                                            <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                <FaFileImage className="me-1" /> GIF
                                            </Badge>
                                        </div>
                                        <small className="text-muted mt-2">
                                            Max: 10 Mo
                                        </small>
                                    </>
                                )}
                            </div>

                            {/* Informations du fichier sélectionné */}
                            {selectedFile && (
                                <div className="mt-3 d-flex flex-wrap gap-2 justify-content-center align-items-center">
                                    <div className="text-muted small d-flex align-items-center">
                                        <FaInfoCircle className="me-1" />
                                        {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} Mo)
                                    </div>
                                    <Badge bg="info" className="rounded-pill px-3 py-2">
                                        {selectedFile.type}
                                    </Badge>
                                </div>
                            )}
                        </div>

                        {/* Nom du service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaFileImage className="me-2" />
                                Informations du service
                            </h5>
                            <div className="form-group">
                                <label className="fw-bold small text-muted mb-1">
                                    Nom du service *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={newService.name}
                                    onChange={handleInputChange}
                                    className="form-control"
                                    placeholder="Ex: Garde d'enfant"
                                    disabled={isCreating || isUploading}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        padding: '10px 14px'
                                    }}
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label className="fw-bold small text-muted mb-1">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={newService.description}
                                    onChange={handleInputChange}
                                    className="form-control"
                                    rows="3"
                                    placeholder="Description détaillée du service..."
                                    disabled={isCreating || isUploading}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        padding: '10px 14px'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Récapitulatif */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-secondary">
                                <FaInfoCircle className="me-2" />
                                Récapitulatif
                            </h5>
                            <div className="bg-white rounded-3 p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="text-muted">Nom :</span>
                                        <strong className="ms-2">
                                            {newService.name || 'Non défini'}
                                        </strong>
                                    </div>
                                    <div>
                                        <span className="text-muted">Image :</span>
                                        <strong className="ms-2">
                                            {selectedFile ? (
                                                <span className="text-success">
                                                    <FaCheck className="me-1" /> Sélectionnée
                                                </span>
                                            ) : (
                                                <span className="text-muted">Aucune</span>
                                            )}
                                        </strong>
                                    </div>
                                </div>
                                {newService.description && (
                                    <div className="mt-2 pt-2 border-top">
                                        <small className="text-muted">
                                            <FaInfoCircle className="me-1" />
                                            {newService.description}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer fixe avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px',
                        borderBottomLeftRadius: '24px',
                        borderBottomRightRadius: '24px'
                    }}>
                        <div className="d-flex justify-content-end gap-3">
                            <button
                                className="btn btn-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={handleClose}
                                disabled={isCreating || isUploading}
                                type="button"
                            >
                                <FaTimes /> Annuler
                            </button>
                            <button
                                className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={handleSubmit}
                                disabled={isCreating || isUploading || !newService.name.trim()}
                                type="button"
                            >
                                {isUploading && (
                                    <>
                                        <FaSpinner className="fa-spin me-2" />
                                        Upload en cours...
                                    </>
                                )}
                                {isCreating && !isUploading && (
                                    <>
                                        <FaSpinner className="fa-spin me-2" />
                                        Création en cours...
                                    </>
                                )}
                                {!isCreating && !isUploading && (
                                    <>
                                        <FaUpload /> Créer le service
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles CSS intégrés */}
            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .service-create-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                .form-control:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }

                /* Styles du Drop Zone */
                .drop-zone {
                    transition: all 0.3s ease;
                    border: 2px dashed #cbd5e0;
                    border-radius: 12px;
                    padding: 20px;
                    text-align: center;
                    background-color: white;
                    cursor: pointer;
                    min-height: 200px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .drop-zone:hover {
                    border-color: #28a745;
                    background-color: rgba(40, 167, 69, 0.03);
                }

                .drop-zone-active {
                    border-color: #28a745 !important;
                    background-color: rgba(40, 167, 69, 0.08) !important;
                    transform: scale(1.02);
                }

                .drop-zone-has-image {
                    padding: 12px;
                    min-height: auto;
                }

                .drop-zone img {
                    max-width: 100%;
                    max-height: 200px;
                    object-fit: contain;
                    border-radius: 8px;
                }

                .drop-zone .badge {
                    font-size: 0.7rem;
                    padding: 6px 12px;
                }

                /* Animation d'entrée */
                .bg-white.rounded-3.p-3 {
                    animation: fadeIn 0.3s ease;
                }
            `}</style>
        </>
    );
};

export default ServiceHDSCreateModal;